import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';

// Styles d'animation inline
const modalAnimation = {
  animation: 'fadeInSlideDown 0.2s ease-out',
};

// Injecter les styles d'animation dans le document
if (typeof document !== 'undefined' && !document.getElementById('notification-modal-styles')) {
  const style = document.createElement('style');
  style.id = 'notification-modal-styles';
  style.textContent = `
    @keyframes fadeInSlideDown {
      from {
        opacity: 0;
        transform: translateY(-8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;
  document.head.appendChild(style);
}

const NotificationModal = ({ isOpen, onClose }) => {
  const { notifications, unreadCount, markAsRead, clearAll } = useNotifications();
  const modalRef = useRef(null);

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    // Optionnel : rediriger vers la page concernée
    if (notification.data?.request_id) {
      window.location.href = `/admin/contact-requests/${notification.data.request_id}`;
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_contact_request':
        return '📬';
      case 'visit_scheduled':
        return '📅';
      case 'request_assigned':
        return '👤';
      case 'visit_cancelled':
        return '❌';
      default:
        return '🔔';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000); // en secondes

    if (diff < 60) return 'À l\'instant';
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // Fermer avec Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Empêcher le scroll du body quand le modal est ouvert
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[1300] transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="fixed right-6 top-16 w-96 bg-white rounded-2xl shadow-2xl border border-neutral-200 z-[1400] max-h-[600px] flex flex-col overflow-hidden"
        style={modalAnimation}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Notifications
            {unreadCount > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({unreadCount} non lue{unreadCount > 1 ? 's' : ''})
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            {notifications.length > 0 && (
              <button
                onClick={clearAll}
                className="text-xs text-gray-600 hover:text-gray-800 transition-colors"
              >
                Effacer tout
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Fermer"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Liste de notifications */}
        <div className="overflow-y-auto flex-1 rounded-b-2xl" style={{ maxHeight: '480px' }}>
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Aucune notification</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 cursor-pointer transition-colors hover:bg-gray-50 ${
                    !notification.read ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notification.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                        {notification.message}
                      </p>
                      {notification.data && (
                        <div className="mt-1 text-xs text-gray-500">
                          {notification.data.seeker_name && (
                            <span>Contact: {notification.data.seeker_name}</span>
                          )}
                        </div>
                      )}
                      <p className="mt-1 text-xs text-gray-400">
                        {formatTime(notification.timestamp)}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default NotificationModal;

